/**
 * Credits section of the billing page: types and copy. Pure, so client
 * components and stories can import it; `get-credits-overview.ts`
 * fetches the data.
 */

import type { Database } from "@/lib/supabase/database.types";
import { BillingFeature } from "@/lib/billing/constants";
import { getFeatureDisplayName } from "@/lib/billing/feature-limits";

export type CreditEntryKind = Database["public"]["Enums"]["credit_entry_kind"];

export interface CreditPack {
  priceId: string;
  credits: number;
  unitAmountCents: number;
  /** ISO currency code, lower case as Stripe returns it */
  currency: string;
}

export interface CreditEntry {
  id: string;
  kind: CreditEntryKind;
  /** Signed change to the balance */
  delta: number;
  createdAt: string;
  feature: BillingFeature | null;
}

export interface CreditsOverview {
  balance: number;
  /**
   * Packs the viewer can buy. Empty for free orgs, for non-owners, and
   * when no pack is on sale in Stripe.
   */
  packs: CreditPack[];
  /** Why `packs` is empty, when the viewer would otherwise see them */
  unavailableReason: "free_plan" | "not_owner" | "none_on_sale" | null;
  /** Most recent ledger entries, newest first */
  recent: CreditEntry[];
}

/** "$175", "$3,000", "$12.50": whole amounts drop the cents. */
export function formatPackPrice(pack: CreditPack): string {
  const whole = pack.unitAmountCents % 100 === 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: pack.currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(pack.unitAmountCents / 100);
}

export function formatCredits(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

/** "+1,000", "−24": the sign is always shown. */
export function formatCreditDelta(delta: number): string {
  return `${delta > 0 ? "+" : "−"}${formatCredits(Math.abs(delta))}`;
}

const KIND_LABEL: Record<CreditEntryKind, string> = {
  purchase: "Credit pack purchased",
  grant: "Credits granted",
  adjustment: "Balance adjustment",
  consume: "Used",
  refund: "Returned (unused)",
  reversal: "Removed after a payment refund",
};

/** One line of history, e.g. "Used: Query discovery". */
export function describeCreditEntry(entry: CreditEntry): string {
  const label = KIND_LABEL[entry.kind];
  return entry.feature
    ? `${label}: ${getFeatureDisplayName(entry.feature)}`
    : label;
}

export interface ScanCostSummary {
  includedRuns: number;
  creditRuns: number;
  credits: number;
  /** Prompts the scan runs. */
  activePrompts: number;
  /** The plan's prompts-per-project cap; null means unlimited. */
  promptCap: number | null;
}

/**
 * Why a scan costs what it does, in runs. One sentence for the scan
 * routes' 402 and the scan button's confirmation, so they can't drift.
 */
export function explainScanCost(cost: ScanCostSummary): string {
  const runs = cost.includedRuns + cost.creditRuns;
  const why = `This scan needs ${formatCredits(runs)} ${runs === 1 ? "run" : "runs"}; your plan's monthly allowance covers ${formatCredits(cost.includedRuns)}, so it costs ${formatCredits(cost.credits)} ${cost.credits === 1 ? "credit" : "credits"} (one per run).`;
  const aboveCap =
    cost.promptCap === null
      ? 0
      : Math.max(0, cost.activePrompts - cost.promptCap);
  if (aboveCap === 0) return why;
  return `${why} Runs for the ${formatCredits(aboveCap)} ${aboveCap === 1 ? "prompt" : "prompts"} above your plan's ${formatCredits(cost.promptCap!)}-prompt limit always cost credits.`;
}

/**
 * The scan button's confirmation copy for a scan that costs credits,
 * from the cost preview (GET /api/visibility/scan/cost).
 */
export function describeScanCost(
  cost: ScanCostSummary & { balance: number },
): string {
  const why = explainScanCost(cost);
  const balance = `Your org has ${formatCredits(cost.balance)}.`;
  return cost.credits > cost.balance
    ? `${why} ${balance} Buy credits to run it.`
    : `${why} ${balance}`;
}
